import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://mhglobalmarket.de";
const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/rfq6GFxLxdhr5INc9Y4gfziKUJn2/social-images/social-1770124035661-GlobalMarket%20Logo%20(2).png";

const OG_LOCALES: Record<string, string> = { de: "de_DE", en: "en_US", es: "es_ES" };

/** Turn a relative asset path or path-only URL into an absolute canonical URL. */
function toAbsolute(value: string) {
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
}

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: "website" | "article";
  image?: string;
  noIndex?: boolean;
}

export function SEOHead({
  title,
  description,
  canonical,
  type = "website",
  image = DEFAULT_OG_IMAGE,
  noIndex = false,
}: SEOHeadProps) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();

  // Per-route canonical / og:url — self-referencing for every page.
  const pageUrl = canonical
    ? toAbsolute(canonical)
    : toAbsolute(pathname === "/" ? "/" : pathname.replace(/\/+$/, ""));
  const imageUrl = toAbsolute(image);

  const seoTitle = title || t("seo.title");
  const seoDescription = description || t("seo.description");
  const fullTitle = title ? `${title} | M&H Global Market` : seoTitle;

  // Block indexing on any non-canonical host (e.g. *.lovable.app preview/staging)
  const isLovableHost =
    typeof window !== "undefined" && /lovable\.app$/i.test(window.location.hostname);
  const shouldNoIndex = noIndex || isLovableHost;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={i18n.language} dir={i18n.language === "ar" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={pageUrl} />
      <meta name="robots" content={shouldNoIndex ? "noindex, nofollow" : "index, follow"} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={OG_LOCALES[i18n.language?.slice(0, 2)] ?? "de_DE"} />
      <meta property="og:site_name" content="M&H Global Market" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* GEO Tags */}
      <meta name="geo.region" content="DE-NI" />
      <meta name="geo.placename" content="Oldenburg" />
      <meta name="geo.position" content="53.1435;8.2146" />
      <meta name="ICBM" content="53.1435, 8.2146" />

      {/* Additional SEO */}
      <meta name="author" content="M&H Global Market GmbH" />
      <meta name="publisher" content="M&H Global Market GmbH" />
      <meta name="keywords" content={t("seo.keywords")} />

      {/* Alternate Languages */}
      <link rel="alternate" hrefLang="de" href={`${pageUrl}?lng=de`} />
      <link rel="alternate" hrefLang="en" href={`${pageUrl}?lng=en`} />
      <link rel="alternate" hrefLang="es" href={`${pageUrl}?lng=es`} />
      <link rel="alternate" hrefLang="x-default" href={pageUrl} />
    </Helmet>
  );
}
